'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

import { Button, Typography } from 'lighthouse';

import AttributeFiltersView from '@/widgets/AttributeFilters/AttributeFiltersView';

import { useCategoryAttributeFilters } from '@/hooks/filters/useCategoryAttributeFilters';

import { ATTRIBUTE_FILTER_COPY } from '@/utilities/attributeFilters/constants';

import { useCityStore } from '@/stores/city/useCityStore';

import { allSparePartsHref, CAMPAIGN_SPARE_PARTS_PRODUCTS_SIZE } from '@/constants/batteryToBattery';
import { CATEGORIES, ENGINE_OIL_CATEGORY_ID } from '@/constants/productCategory';
import { DEFAULT_PRODUCT_OFFER_SORT_BY } from '@/constants/productSort';

import classes from '../../index.module.css';
import CampaignProductCard from '../BatteryProducts/CampaignProductCard';
import Reveal from '../Reveal';
import { SparePartsProductsStatus, SparePartsProductType } from './models';

import { getFilteredProductsService } from '@/client/services/vendor/methods';
import { OfferContent, ProductOfferSortBy } from '@/server/services/vendor/models';
import { ArrowLeft, Drop } from 'iconsax-react';

const mapOfferToCampaignProduct = (offer: OfferContent): SparePartsProductType =>
    ({
        uuid: offer.productUuid,
        slug: offer.productSlug || 'product',
        title: offer.productTitle,
        brand: { id: offer.brandId || 0, name: offer.brandName || '' },
        category: { id: offer.categoryId || 0, name: '', description: '', slug: '', parentId: 0, seoMetadata: {}, breadcrumbs: [] },
        price: offer.price || 0,
        sellingPrice: offer.sellingPrice || offer.price || 0,
        discountPercentage: offer.discountPercentage || 0,
        available: Boolean(offer.availableQuantity),
        costPrice: 0,
        description: '',
        subTitle: '',
        status: 'PUBLISHED',
        link: '',
        attributes: [],
        seoMetadata: {}
    }) as unknown as SparePartsProductType;

const getSparePartsProducts = async (
    categoryId: number,
    cityId: number,
    attributeFilter: string[] = [],
    sortBy: ProductOfferSortBy = DEFAULT_PRODUCT_OFFER_SORT_BY,
    brandIds: number[] = []
) => {
    const response = await getFilteredProductsService({
        categoryIds: [categoryId],
        ...(brandIds.length ? { brandIds } : {}),
        page: 0,
        size: CAMPAIGN_SPARE_PARTS_PRODUCTS_SIZE,
        sortBy,
        cityId,
        ...(attributeFilter.length ? { attributeFilter } : {})
    });

    return response.data.content
        .filter((product) => product.discountPercentage !== 99 && Boolean(product.availableQuantity))
        .map(mapOfferToCampaignProduct);
};

const ProductCardSkeleton = () => (
    <div className='flex w-[268px] shrink-0 snap-start flex-col overflow-hidden rounded-lg bg-white/[0.05] ring-1 ring-white/10 sm:w-auto'>
        <div className='mx-auto my-6 h-28 w-28 animate-pulse rounded-lg bg-white/10' />
        <div className='flex flex-col gap-2 px-4'>
            <div className='h-4 w-3/4 animate-pulse rounded bg-white/10' />
            <div className='h-3 w-1/2 animate-pulse rounded bg-white/10' />
        </div>
        <div className='flex items-center justify-between p-4'>
            <div className='h-5 w-20 animate-pulse rounded bg-white/10' />
            <div className='h-8 w-16 animate-pulse rounded-lg bg-white/10' />
        </div>
    </div>
);

const SparePartsProducts = () => {
    const cityId = useCityStore((state) => state.cityId);
    const [retryCount, setRetryCount] = useState(0);
    const [activeCategoryId, setActiveCategoryId] = useState(ENGINE_OIL_CATEGORY_ID);
    const [products, setProducts] = useState<SparePartsProductType[]>([]);
    const [status, setStatus] = useState<SparePartsProductsStatus>('loading');
    const [sortBy, setSortBy] = useState<ProductOfferSortBy>(DEFAULT_PRODUCT_OFFER_SORT_BY);
    const attributeFilters = useCategoryAttributeFilters({ categoryId: activeCategoryId, withBrands: true });
    const attributeFilterKey = [attributeFilters.attributeFilterParams.join('|'), attributeFilters.brandIds.join(',')].join('#');

    useEffect(() => {
        if (attributeFilters.isLoading) {
            return;
        }

        let isActive = true;

        setStatus('loading');

        void getSparePartsProducts(activeCategoryId, cityId, attributeFilters.attributeFilterParams, sortBy, attributeFilters.brandIds)
            .then((sparePartsProducts) => {
                if (isActive) {
                    setProducts(sparePartsProducts);
                    setStatus('success');
                }
            })
            .catch(() => {
                if (isActive) {
                    setProducts([]);
                    setStatus('error');
                }
            });

        return () => {
            isActive = false;
        };
    }, [activeCategoryId, cityId, attributeFilterKey, attributeFilters.isLoading, sortBy, retryCount]);

    const handleRetry = () => setRetryCount((count) => count + 1);

    const allSparePartsCategoryHref = `${allSparePartsHref}?categoryId=${activeCategoryId}`;

    return (
        <section className={classes.sparePartsSection}>
            <div className='mx-auto w-full max-w-6xl px-5 py-16'>
                <Reveal>
                    <div className='flex items-end justify-between gap-4'>
                        <div>
                            <Typography variant='h5' component='h2' fontWeight={800} className='flex items-center gap-2 text-white'>
                                <Drop size={26} color='#FBBF24' />
                                روغن موتور و لوازم یدکی با تخفیف
                            </Typography>
                            <Typography variant='body2' component='p' className='mt-2 text-white/60'>
                                روغن، فیلتر، لنت و هرچی ماشینت لازم داره؛ کد تخفیف کمپین روی این محصولات هم اعمال میشه.
                            </Typography>
                        </div>
                        <Link
                            href={allSparePartsCategoryHref}
                            className='hidden shrink-0 items-center gap-1 text-sm font-bold text-primary transition-colors hover:text-white sm:flex'>
                            مشاهده همه لوازم یدکی
                            <ArrowLeft size={16} color='currentcolor' />
                        </Link>
                    </div>
                </Reveal>

                <Reveal>
                    <div className='mt-7'>
                        <AttributeFiltersView
                            filters={attributeFilters}
                            sortBy={sortBy}
                            onSortChange={setSortBy}
                            toolbar={
                                <div
                                    className='nice-scroll -mx-5 flex gap-2 overflow-x-auto px-5 py-1 sm:mx-0 sm:flex-wrap sm:overflow-visible
                                        sm:px-0'>
                                    {CATEGORIES.map((category) => {
                                        const isActive = category.id === activeCategoryId;

                                        return (
                                            <button
                                                key={category.id}
                                                type='button'
                                                aria-pressed={isActive}
                                                onClick={() => setActiveCategoryId(category.id)}
                                                className={`group shrink-0 rounded-full border border-solid px-4 py-2 transition-all duration-200 ${
                                                    isActive
                                                        ? 'border-amber-300/60 bg-amber-400/10 shadow-[0_12px_28px_-12px_rgba(251,191,36,0.45)]'
                                                        : `border-white/10 bg-white/[0.05] hover:-translate-y-0.5 hover:border-white/25
                                                            hover:bg-white/10`
                                                }`}>
                                                <Typography
                                                    variant='caption'
                                                    component='span'
                                                    fontWeight={isActive ? 800 : 500}
                                                    className={`whitespace-nowrap leading-4 transition-colors duration-200 ${
                                                        isActive ? 'text-amber-300' : 'text-white/70 group-hover:text-white'
                                                    }`}>
                                                    {category.title}
                                                </Typography>
                                            </button>
                                        );
                                    })}
                                </div>
                            }>
                            <div
                                className='nice-scroll -mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-2
                                    sm:overflow-visible sm:px-0 lg:grid-cols-3'>
                                {status === 'loading' && [1, 2, 3, 4].map((item) => <ProductCardSkeleton key={item} />)}

                                {status === 'success' && products.map((product) => <CampaignProductCard key={product.uuid} product={product} />)}

                                {status === 'success' && !products.length && (
                                    <div className='col-span-full flex w-full flex-col items-center py-10'>
                                        <Typography variant='body2' component='p' className='text-white/50'>
                                            {attributeFilters.activeCount
                                                ? ATTRIBUTE_FILTER_COPY.emptyResults
                                                : 'فعلاً محصولی توی این دسته موجود نیست.'}
                                        </Typography>
                                        <Link href={allSparePartsCategoryHref} className='mt-3 text-sm font-bold text-primary'>
                                            مشاهده صفحه لوازم یدکی و روغن
                                        </Link>
                                    </div>
                                )}

                                {status === 'error' && (
                                    <div className='col-span-full flex w-full flex-col items-center py-10'>
                                        <Typography variant='body2' component='p' className='text-white/50'>
                                            در دریافت لیست محصولات خطایی رخ داد.
                                        </Typography>
                                        <Button variant='outlined' size='small' onClick={handleRetry} className='mt-4 border-white/30 text-white'>
                                            تلاش مجدد
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </AttributeFiltersView>
                    </div>
                </Reveal>

                <Link
                    href={allSparePartsCategoryHref}
                    className='mt-6 flex items-center justify-center gap-1 text-sm font-bold text-primary sm:hidden'>
                    مشاهده همه لوازم یدکی
                    <ArrowLeft size={16} color='currentcolor' />
                </Link>
            </div>
        </section>
    );
};

export default SparePartsProducts;
